import React, { useState } from "react";
import { Copy, Plus, RotateCcw, Trash2 } from "lucide-react";
import { CELL, CELLS, DAYS, DEFAULT_WEEKLY, GRID_END, cellsToWindows, fmt12, toHM, toMin, windowsToCells } from "./scheduleUtils";

const DAY_FULL = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const TIMES = [...CELLS.map(toHM), toHM(GRID_END)];                      // 07:00 … 22:00 in 30-min steps
const normalize = (windows) => cellsToWindows(windowsToCells(windows));  // merge overlaps, drop empties
const nextFree = (windows) => {                                          // first free 1-hour range after the last window
  const start = windows.length ? toMin(windows[windows.length - 1][1]) + CELL : 10 * 60;
  return start + 2 * CELL <= GRID_END ? [toHM(start), toHM(start + 2 * CELL)] : null;
};

function TimeSelect({ value, onChange, min, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="h-10 rounded-xl border border-slate-200 bg-white px-1.5 text-sm font-semibold text-[#0A2540] min-w-0 flex-1">
      {TIMES.filter((t) => (min ? toMin(t) > toMin(min) : toMin(t) < GRID_END)).map((t) => <option key={t} value={t}>{fmt12(t).toUpperCase()}</option>)}
    </select>
  );
}

/** Phone-friendly alternative to the grid: per-day "start — end" rows, 30-min steps, same windows model. */
export function RangeEditor({ weekly, onChange }) {
  const [open, setOpen] = useState("mon");
  const setDay = (d, windows) => onChange({ ...weekly, [d]: normalize(windows) });
  const edit = (d, i, pos, value) => {
    const w = weekly[d].map((r) => [...r]);
    w[i][pos] = value;
    if (toMin(w[i][1]) <= toMin(w[i][0])) w[i][1] = toHM(Math.min(GRID_END, toMin(w[i][0]) + CELL));
    setDay(d, w);
  };
  return (
    <div data-testid="range-editor">
      <div className="flex flex-wrap gap-2 mb-3">
        <button type="button" onClick={() => onChange({ ...weekly, tue: weekly.mon, wed: weekly.mon, thu: weekly.mon, fri: weekly.mon })} data-testid="range-copy-monday"
                className="text-xs font-bold text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"><Copy size={12} /> Copy Monday to Mon–Fri</button>
        <button type="button" onClick={() => onChange({ ...weekly, sat: weekly.mon, sun: weekly.mon })} data-testid="range-copy-monday-weekend"
                className="text-xs font-bold text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"><Copy size={12} /> Copy Monday to Sat–Sun</button>
        <button type="button" onClick={() => onChange({ ...DEFAULT_WEEKLY })} data-testid="range-reset-default"
                className="text-xs font-bold text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"><RotateCcw size={12} /> Reset to default</button>
      </div>
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden">
        {DAYS.map((d) => {
          const windows = weekly[d] || [];
          const isOpen = open === d;
          return (
            <li key={d} className="bg-white" data-testid={`range-day-${d}`}>
              <button type="button" onClick={() => setOpen(isOpen ? null : d)} className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left" data-testid={`range-toggle-${d}`} aria-expanded={isOpen}>
                <span className="font-extrabold text-[#0A2540] shrink-0">{DAY_FULL[d]}</span>
                <span className={`text-xs font-semibold truncate min-w-0 ${windows.length ? "text-slate-600" : "text-slate-400"}`} data-testid={`range-summary-${d}`}>
                  {windows.length ? windows.map(([a, b]) => `${fmt12(a).toUpperCase()} – ${fmt12(b).toUpperCase()}`).join(", ") : "Off"}
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-2" data-testid={`range-rows-${d}`}>
                  <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Available hours</p>
                  {windows.length === 0 && <p className="text-sm text-slate-400">Day off — add a time range to open this day.</p>}
                  {windows.map(([a, b], i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`range-row-${d}-${i}`}>
                      <TimeSelect value={a} onChange={(v) => edit(d, i, 0, v)} testId={`range-start-${d}-${i}`} />
                      <span className="text-slate-400 font-bold">—</span>
                      <TimeSelect value={b} min={a} onChange={(v) => edit(d, i, 1, v)} testId={`range-end-${d}-${i}`} />
                      <button type="button" onClick={() => setDay(d, windows.filter((_, j) => j !== i))} aria-label="Remove range" data-testid={`range-remove-${d}-${i}`}
                              className="h-10 w-10 grid place-items-center rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  {nextFree(windows) && (
                    <button type="button" onClick={() => setDay(d, [...windows, nextFree(windows)])} data-testid={`range-add-${d}`}
                            className="text-xs font-bold text-[#0A2540] inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-3 py-1.5 hover:bg-slate-50"><Plus size={12} /> Add another time range</button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
